import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ArrowRight, HeartHandshake, PawPrint, ShieldCheck, Sparkles } from "lucide-react";

const STATS = [
  { k: "412", v: "Dogs cared for this year" },
  { k: "6,800", v: "Volunteer hours logged" },
  { k: "38", v: "Weekly open shifts" },
];

export default function Landing() {

  return (
    <div className="min-h-screen bg-[color:var(--cream)]">
      <Navbar />

      {/* Hero — dark rounded panel like HRAAD's site */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-6">
        <div className="dark-panel relative overflow-hidden p-8 lg:p-14">
          <div className="absolute -top-10 -right-10 h-72 w-72 rounded-full bg-[color:var(--red)]/25 blur-3xl" />
          <div className="absolute -bottom-16 -left-10 h-72 w-72 rounded-full bg-[color:var(--yellow)]/20 blur-3xl" />

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <h1 data-testid="hero-headline" className="font-display text-3xl sm:text-4xl lg:text-5xl text-[color:var(--cream)] max-w-2xl">Get involved!</h1>
              <p className="mt-6 text-lg sm:text-xl text-[color:var(--cream-2)]/90 max-w-xl leading-relaxed">
                Sign up for kennel shifts, dog walks, adoption events and more. Sign our waiver once, then pick up a spot any week.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-volunteer-signup-button" className="btn-pill btn-primary inline-flex items-center gap-2">
                  Become a volunteer <ArrowRight className="h-4 w-4" />
                </Link>
                <button
                  data-testid="hero-existing-volunteer-btn"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    const redirectUrl = window.location.origin + "/auth/callback";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="btn-pill text-sm border-2 border-[color:var(--cream-2)]/40 text-[color:var(--cream)] hover:bg-white/10 inline-flex items-center gap-2"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.23 1.5-1.7 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.95 0-3.28 2.63-5.95 5.85-5.95 1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.86 3.9 14.68 3 12 3 6.98 3 2.93 7.03 2.93 12s4.05 9 9.07 9c5.24 0 8.71-3.68 8.71-8.86 0-.6-.06-1.05-.15-1.5z"/></svg>
                  Existing Volunteers
                </button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] border border-white/10">
                <img src="https://customer-assets-0z36b82j.emergentagent.net/job_dog-care-signup/artifacts/514553800aa4e499_IMG_5779.webp" alt="Two huskies at the Hood River Adopt A Dog mosaic fountain" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl max-w-2xl">Three steps to spend a shift with the dogs.</h2>
          <p className="text-[color:var(--stone)] max-w-sm">All new volunteers sign our one-page liability waiver.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: <PawPrint className="h-5 w-5" />, title: "1. Create your profile", body: "Sign up with email or your Google account. It takes about 40 seconds." },
            { icon: <ShieldCheck className="h-5 w-5" />, title: "2. Sign the waiver", body: "Type your name, add emergency contact info. Signed once, valid all year." },
            { icon: <Sparkles className="h-5 w-5" />, title: "3. Pick a shift", body: "Browse the calendar and claim a spot. We'll email if urgent help is needed." },
          ].map((c, i) => (
            <div key={c.title} className="card-warm p-7" data-testid={`how-step-${i+1}`}>
              <div className="h-10 w-10 rounded-full bg-[color:var(--cream-2)] flex items-center justify-center mb-4">{c.icon}</div>
              <div className="font-display text-lg">{c.title}</div>
              <p className="text-sm text-[color:var(--stone)] mt-2 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Roles */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-16">
        <h2 className="font-display text-3xl sm:text-4xl mb-8">What you can help with</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { t: "Dog Walking", img: "https://images.unsplash.com/photo-1763705861556-3a6642437bf2?crop=entropy&cs=srgb&fm=jpg&q=85" },
            { t: "Kennel Care", img: "https://images.unsplash.com/photo-1534822916-212967756e82?crop=entropy&cs=srgb&fm=jpg&q=85" },
            { t: "Special Events", img: "https://images.unsplash.com/photo-1551730459-92db2a308d6a?crop=entropy&cs=srgb&fm=jpg&q=85" },
            { t: "Recycling Fundraising", img: "https://images.unsplash.com/photo-1684569680520-5e3c52c1b666?crop=entropy&cs=srgb&fm=jpg&q=85" },
          ].map((r) => (
            <div key={r.t} className="relative rounded-2xl overflow-hidden aspect-[4/5]" data-testid={`role-card-${r.t.toLowerCase().replace(/\s+/g,'-')}`}>
              <img src={r.img} alt={r.t} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--ink)]/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="font-display text-xl text-white">{r.t}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-20">
      </section>

    </div>
  );
}
